import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { MFA_LOGIN_V2, SMS_FACTOR } from "../../../../config/featureFlags";
import { getStatus } from "../../../../services/mfa/mfaSettingsApi";
import { clearNudgePending, isNudgePending, isSnoozed, snoozeNudge } from "./nudgeStorage";

const NEEDS_SETUP = ["NOT_CONFIGURED", "DISABLED", "PENDING"];
// docs/specs/sms-factor.md §1: with SMS_FACTOR and an `sms` status from node, the prompt offers the choice and
// shows only while neither factor is ACTIVE. Otherwise today's rule, unchanged.
const offersSms = (body) => SMS_FACTOR && Boolean(body?.sms?.status);
const needsPrompt = (body) =>
  NEEDS_SETUP.includes(body?.authenticator?.status) && !(offersSms(body) && body.sms.status === "ACTIVE");

// Decides whether the post-login prompt shows (spec "When it shows"): flag on, a sign-in just finished in this
// tab, not a masquerade session, not snoozed, and the authenticator is not ACTIVE. Any error = no prompt.
// The pending flag is cleared only once a run has decided, so React StrictMode's double effect still prompts once.
const useMfaNudge = () => {
  const masquerade = useSelector((state) => state.RS_Authentication?.masquerade?.isIt);
  const [open, setOpen] = useState(false);
  const [offerSms, setOfferSms] = useState(false);
  const userid = localStorage.getItem("apex_userid");

  useEffect(() => {
    if (!MFA_LOGIN_V2 || !isNudgePending()) return undefined;
    let cancelled = false;
    const decide = (show) => {
      if (cancelled) return;
      clearNudgePending();
      setOpen(show);
    };
    if (masquerade || !userid || isSnoozed(userid)) {
      decide(false);
      return () => { cancelled = true; };
    }
    getStatus()
      .then((body) => {
        if (!cancelled) setOfferSms(offersSms(body));
        decide(needsPrompt(body));
      })
      .catch((e) => {
        console.error(`2FA nudge: status failed (${e?.response?.status ?? "no response"}): ${e?.message}`);
        decide(false);
      });
    return () => { cancelled = true; };
  }, [masquerade, userid]);

  const close = useCallback(() => setOpen(false), []);
  const skip = useCallback(() => {
    snoozeNudge(userid);
    setOpen(false);
  }, [userid]);

  return { open, offerSms, skip, close };
};

export default useMfaNudge;
